/**
 * Calendar-aware date parts ↔ ISO conversion. Wire values stay ISO
 * 'YYYY-MM-DD' (calendar-independent, matching the backend); Jalali vs
 * Gregorian is presentation only, per the clinic's configured system.
 */

import { CalendarDate, createCalendar, parseDate, toCalendar } from '@internationalized/date';
import type { CalendarSystem } from '../clinic/api';

export interface DateParts {
  year: number;
  month: number; // 1-based in the DISPLAY calendar
  day: number;
}

function cal(system: CalendarSystem) {
  return createCalendar(system === 'jalali' ? 'persian' : 'gregory');
}

const gregorian = createCalendar('gregory');

/** ISO 'YYYY-MM-DD' → parts in the display calendar. Returns null on bad input. */
export function isoToParts(iso: string, system: CalendarSystem): DateParts | null {
  try {
    const g = parseDate(iso.slice(0, 10));
    const d = toCalendar(g, cal(system));
    return { year: d.year, month: d.month, day: d.day };
  } catch {
    return null;
  }
}

/** Display-calendar parts → ISO 'YYYY-MM-DD'. Returns null when invalid. */
export function partsToIso(parts: DateParts, system: CalendarSystem): string | null {
  try {
    // CalendarDate silently constrains out-of-range parts; reject them instead.
    const c = cal(system);
    const probe = new CalendarDate(c, parts.year, 1, 1);
    if (parts.month < 1 || parts.month > c.getMonthsInYear(probe)) return null;
    const monthProbe = new CalendarDate(c, parts.year, parts.month, 1);
    if (parts.day < 1 || parts.day > c.getDaysInMonth(monthProbe)) return null;
    const d = new CalendarDate(c, parts.year, parts.month, parts.day);
    const g = toCalendar(d, gregorian);
    const mm = String(g.month).padStart(2, '0');
    const dd = String(g.day).padStart(2, '0');
    return `${g.year}-${mm}-${dd}`;
  } catch {
    return null;
  }
}

export function daysInMonth(year: number, month: number, system: CalendarSystem): number {
  try {
    return new CalendarDate(cal(system), year, month, 1).calendar.getDaysInMonth(
      new CalendarDate(cal(system), year, month, 1),
    );
  } catch {
    return 31;
  }
}

/** Current year in the display calendar (for building year ranges). */
export function currentYear(system: CalendarSystem): number {
  const now = new Date();
  const iso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return isoToParts(iso, system)?.year ?? now.getFullYear();
}

/** Localized month names for the display calendar. */
export function monthNames(system: CalendarSystem, locale: 'en' | 'fa'): string[] {
  const base = locale === 'fa' ? 'fa-IR' : 'en-US';
  const tag = `${base}-u-ca-${system === 'jalali' ? 'persian' : 'gregory'}`;
  const fmt = new Intl.DateTimeFormat(tag, { month: 'long' });
  const year = currentYear(system);
  return Array.from({ length: 12 }, (_, i) => {
    const iso = partsToIso({ year, month: i + 1, day: 2 }, system);
    return iso ? fmt.format(new Date(`${iso}T12:00:00Z`)) : String(i + 1);
  });
}
